/**
 * Pure helpers for the automations builder: the trigger/action catalogs, which
 * columns each can target, blank-rule factories, and a plain-English summary of
 * a rule. Kept UI-free so it's unit-testable and shared by the panel + builder.
 *
 * Types come from the generated `entities/tracker-automation` DTOs, so the
 * builder always speaks the exact shape the backend validates.
 */
import type { TrackerBoardType } from '@/entities/tracker';
import type {
  AutomationAction,
  AutomationApprovalState,
  AutomationConditions,
  AutomationTrigger,
  AutomationValueOperator,
} from '@/entities/tracker-automation';
import type { ParseKeys } from 'i18next';

import { PROJECT_TYPE_MAP } from '@/entities/project';
import { i18n } from '@/shared/i18n';
import { z } from 'zod';

import {
  compileConditions,
  conditionsAreReadable,
  type RuleConditions,
} from '@inference.engine/tracker-filters/web';

import {
  DATE_BUCKETS,
  filterKindForColumnType,
  filterMatchColumns,
  type DateBucket,
} from './filtering';
import { STATUS_COLORS } from './board-columns';
import { recordFieldsFor } from './record-fields';
import { recordStatusOptions } from './record-status-options';
import type { CustomColumn, StatusOption } from './useTrackersBoard';

export type TriggerType = AutomationTrigger['type'];
export type ApprovalEvent = NonNullable<AutomationTrigger['approvalEvent']>;
export type ActionType = AutomationAction['type'];

/** Triggers offered in the builder (date_arrives is a later, time-based phase). */
export const TRIGGER_OPTIONS: ReadonlyArray<{
  type: TriggerType;
  /** Resolved by the rendering component so the label follows the active locale. */
  labelKey: ParseKeys<'trackers'>;
}> = [
  {
    type: 'status_changed',
    labelKey: 'trackersBoard.automationModel.triggerOptions.aStatusChanges',
  },
  {
    type: 'checkbox_changed',
    labelKey: 'trackersBoard.automationModel.triggerOptions.aCheckboxIsCheckedUnchecked',
  },
  {
    type: 'button_clicked',
    labelKey: 'trackersBoard.automationModel.triggerOptions.aButtonIsClicked',
  },
  { type: 'people_changed', labelKey: 'trackersBoard.automationModel.triggerOptions.peopleChange' },
  {
    type: 'value_changed',
    labelKey: 'trackersBoard.automationModel.triggerOptions.aFieldValueChanges',
  },
  { type: 'date_arrives', labelKey: 'trackersBoard.automationModel.triggerOptions.aDateArrives' },
  {
    type: 'item_created',
    labelKey: 'trackersBoard.automationModel.triggerOptions.anItemIsCreated',
  },
  {
    type: 'approval_changed',
    labelKey: 'trackersBoard.automationModel.triggerOptions.anApprovalChanges',
  },
  { type: 'files_added', labelKey: 'trackersBoard.automationModel.triggerOptions.aFileIsAdded' },
  { type: 'schedule', labelKey: 'trackersBoard.automationModel.triggerOptions.onASchedule' },
  {
    type: 'approval_event',
    labelKey: 'trackersBoard.automationModel.triggerOptions.anApprovalIsDecided',
  },
];

/**
 * The decisions an `approval_event` trigger can wait for: `labelKey` names the
 * picker option, `summaryKey` the phrase the rule's sentence reads.
 */
export const APPROVAL_EVENT_OPTIONS: ReadonlyArray<{
  event: ApprovalEvent;
  labelKey: ParseKeys<'trackers'>;
  summaryKey: ParseKeys<'trackers'>;
}> = [
  {
    event: 'step_completed',
    labelKey: 'trackersBoard.automationRuleFields.approvalOptions.stepCompleted',
    summaryKey: 'trackersBoard.automationModel.approvalEvents.stepCompleted',
  },
  {
    event: 'approved',
    labelKey: 'trackersBoard.automationRuleFields.approvalOptions.approved',
    summaryKey: 'trackersBoard.automationModel.approvalEvents.approved',
  },
  {
    event: 'changes_requested',
    labelKey: 'trackersBoard.automationRuleFields.approvalOptions.changesRequested',
    summaryKey: 'trackersBoard.automationModel.approvalEvents.changesRequested',
  },
];

/** Actions offered in the builder. `move_to_group` only on free-form boards. */
export const ACTION_OPTIONS: ReadonlyArray<{
  type: ActionType;
  labelKey: ParseKeys<'trackers'>;
}> = [
  { type: 'set_status', labelKey: 'trackersBoard.automationModel.actionOptions.setAStatus' },
  { type: 'move_to_group', labelKey: 'trackersBoard.automationModel.actionOptions.moveToAGroup' },
  { type: 'set_checkbox', labelKey: 'trackersBoard.automationModel.actionOptions.checkUncheck' },
  {
    type: 'set_date_today',
    labelKey: 'trackersBoard.automationModel.actionOptions.setADateToToday',
  },
  { type: 'assign_people', labelKey: 'trackersBoard.automationModel.actionOptions.assignPeople' },
  { type: 'set_number', labelKey: 'trackersBoard.automationModel.actionOptions.setANumber' },
  { type: 'set_text', labelKey: 'trackersBoard.automationModel.actionOptions.setText' },
  { type: 'clear_value', labelKey: 'trackersBoard.automationModel.actionOptions.clearAField' },
  { type: 'shift_date', labelKey: 'trackersBoard.automationModel.actionOptions.shiftADate' },
  {
    type: 'increment_number',
    labelKey: 'trackersBoard.automationModel.actionOptions.incrementANumber',
  },
  { type: 'copy_value', labelKey: 'trackersBoard.automationModel.actionOptions.copyAValue' },
  { type: 'notify', labelKey: 'trackersBoard.automationModel.actionOptions.notifyPeople' },
  { type: 'comment', labelKey: 'trackersBoard.automationModel.actionOptions.commentOnTheRow' },
  { type: 'webhook', labelKey: 'trackersBoard.automationModel.actionOptions.sendAWebhook' },
  { type: 'slack', labelKey: 'trackersBoard.automationModel.actionOptions.postToSlack' },
  { type: 'create_row', labelKey: 'trackersBoard.automationModel.actionOptions.createARow' },
  {
    type: 'duplicate_row',
    labelKey: 'trackersBoard.automationModel.actionOptions.duplicateTheRow',
  },
  { type: 'move_row', labelKey: 'trackersBoard.automationModel.actionOptions.moveTheRow' },
  { type: 'archive_row', labelKey: 'trackersBoard.automationModel.actionOptions.archiveTheRow' },
  { type: 'ai_fill', labelKey: 'trackersBoard.automationAi.fill.option' },
  { type: 'run_agent', labelKey: 'trackersBoard.automationAi.agent.option' },
];

/** The server's cap on what a rule asks an agent. */
export const MAX_AGENT_INPUT_LENGTH = 2000;

export type AiMode = NonNullable<AutomationAction['aiMode']>;

/** The column types each AI mode can write; mirrors the server's own table. */
/* eslint-disable i18next/no-literal-string -- column type ids */
export const AI_MODE_COLUMN_TYPES: Record<AiMode, readonly string[]> = {
  classify: ['status'],
  extract: ['text', 'link', 'number', 'date'],
  summarize: ['text'],
};
/* eslint-enable i18next/no-literal-string */

export const AI_MODES = Object.keys(AI_MODE_COLUMN_TYPES) as AiMode[];

/** The server's cap on an AI action's instruction. */
export const MAX_AI_INSTRUCTION_LENGTH = 1000;

/** The columns an AI action in `mode` can write. */
export const aiModeColumns = (mode: AiMode | undefined, cols: CustomColumn[]) =>
  cols.filter((c) => (mode ? AI_MODE_COLUMN_TYPES[mode] : []).includes(c.type));

/** Every column some AI mode can write. */
const aiFillColumns = (cols: CustomColumn[]) =>
  cols.filter((c) => AI_MODES.some((mode) => AI_MODE_COLUMN_TYPES[mode].includes(c.type)));

/** The mode a fresh AI action on `column` starts in: a status is classified, anything else extracted. */
export const defaultAiMode = (column: CustomColumn | undefined): AiMode =>
  column?.type === 'status' ? 'classify' : 'extract';

/** Actions that reach outside the row's cells rather than writing a column. */
export type EffectActionType = Extract<
  ActionType,
  | 'notify'
  | 'comment'
  | 'webhook'
  | 'slack'
  | 'create_row'
  | 'duplicate_row'
  | 'move_row'
  | 'archive_row'
  | 'run_agent'
>;

const EFFECT_ACTIONS: ReadonlySet<ActionType> = new Set<ActionType>([
  'notify',
  'comment',
  'webhook',
  'slack',
  'create_row',
  'duplicate_row',
  'move_row',
  'archive_row',
  'run_agent',
]);

const ROW_ACTIONS: ReadonlySet<ActionType> = new Set<ActionType>([
  'create_row',
  'duplicate_row',
  'move_row',
  'archive_row',
]);

/** Actions that add, copy, move or archive rows: offered on free-form boards only. */
export const isRowAction = (type: ActionType) => ROW_ACTIONS.has(type);

/** What the server's `@IsUrl` takes: https with a host that has a TLD; `new URL` alone accepts `https://exa`. */
const POSTABLE_URL = z.url({ protocol: /^https$/, hostname: z.regexes.domain });
const SLACK_HOST = 'hooks.slack.com';

/**
 * Whether a webhook or Slack action has somewhere to post. A typed URL decides
 * while it is there, so a half-typed one blocks Save rather than falling back
 * to the saved key; an empty field keeps the saved one.
 */
export const actionHasUrl = (action: AutomationAction) => {
  const typed = action.url?.trim();
  if (!typed) return Boolean(action.urlKey);
  if (!POSTABLE_URL.safeParse(typed).success) return false;
  return action.type !== 'slack' || new URL(typed).hostname === SLACK_HOST;
};

export const isEffectAction = (type: ActionType): type is EffectActionType =>
  EFFECT_ACTIONS.has(type);

/** The server's cap on a notify note or a comment's text. */
export const MAX_ACTION_MESSAGE_LENGTH = 2000;
/** What the server takes for a text write or a new row's title, the longest a row title can be. */
export const MAX_ACTION_TEXT_LENGTH = 300;

/** Whether a notify action names anyone to email. */
export const notifyHasRecipients = (action: AutomationAction) =>
  (action.userIds?.length ?? 0) > 0 ||
  (action.recipientRefs?.length ?? 0) > 0 ||
  (action.recipientColumnIds?.length ?? 0) > 0;

/** A column a trigger can watch: the board's own, or a field of the record behind the row. */
export type TriggerColumn = CustomColumn & { source?: 'record' };

export const statusColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'status');
/**
 * Plain single-date columns: the only ones the date-arrives scanner matches, and
 * the only ones `set_date_today` may write, since its single day is not a range.
 */
export const dateOnlyColumns = (cols: CustomColumn[]) => cols.filter((c) => c.type === 'date');
export const checkboxColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'checkbox');
export const peopleColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'people');
export const buttonColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'button');
export const numberColumns = (cols: CustomColumn[]) => cols.filter((c) => c.type === 'number');
/** Text-like columns writable by `set_text` (plain text + link). */
export const textColumns = (cols: CustomColumn[]) =>
  cols.filter((c) => c.type === 'text' || c.type === 'link');
/** Columns read as numbers by a value trigger's operators and by an increment. */
// eslint-disable-next-line i18next/no-literal-string -- column type ids
const NUMERIC_TYPES: ReadonlySet<string> = new Set(['number', 'progress']);

/** Columns whose plain value can drive a `value_changed` trigger. */
export const valueColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => ['number', 'text', 'link', 'team', 'progress'].includes(c.type));
export const approvalColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'approval');
export const filesColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => c.type === 'files');
/** Columns a date rule can watch: plain dates, ranges and timelines. */
export const dateTriggerColumns = <C extends CustomColumn>(cols: C[]) =>
  cols.filter((c) => ['date', 'date_range', 'timeline'].includes(c.type));
/** A date column whose cell has a start and an end to choose between. */
export const hasDateEdges = (column: CustomColumn | undefined) =>
  column?.type === 'date_range' || column?.type === 'timeline';
const shiftableColumns = (cols: CustomColumn[]) =>
  cols.filter((c) => c.type === 'date' || c.type === 'date_range');
const incrementableColumns = (cols: CustomColumn[]) =>
  cols.filter((c) => NUMERIC_TYPES.has(c.type));

/** The operators a value trigger offers on a column, by what its cells hold. */
export function valueOperatorsFor(column: CustomColumn | undefined): AutomationValueOperator[] {
  const numeric = column ? NUMERIC_TYPES.has(column.type) : false;
  return numeric
    ? ['equals', 'greater_than', 'less_than', 'between', 'is_empty', 'is_not_empty']
    : ['equals', 'contains', 'is_empty', 'is_not_empty'];
}

/** Operators that compare against nothing the person types. */
export const operatorTakesValue = (operator: AutomationValueOperator) =>
  operator !== 'is_empty' && operator !== 'is_not_empty';
/** Columns whose value a `clear_value` action can wipe (everything editable). */
export const clearableColumns = (cols: CustomColumn[]) =>
  cols.filter((c) =>
    ['status', 'checkbox', 'date', 'date_range', 'people', 'number', 'text', 'link'].includes(
      c.type,
    ),
  );

/**
 * The columns each action may write. The builder's action list, each row's
 * default target and column picker, the blank action and the completeness
 * check all read this one table, so they cannot disagree about a type.
 */
const ACTION_TARGET_COLUMNS: Record<
  Exclude<ActionType, 'move_to_group' | EffectActionType>,
  (cols: CustomColumn[]) => CustomColumn[]
> = {
  set_status: statusColumns,
  set_checkbox: checkboxColumns,
  set_date_today: dateOnlyColumns,
  assign_people: peopleColumns,
  set_number: numberColumns,
  set_text: textColumns,
  clear_value: clearableColumns,
  shift_date: shiftableColumns,
  increment_number: incrementableColumns,
  copy_value: clearableColumns,
  ai_fill: aiFillColumns,
};

/**
 * The columns a `copy_value` can copy into `target`: the others of its type,
 * since a value means what its column type says it means, and the server
 * refuses a copy across types.
 */
export function copySourceColumns(columns: CustomColumn[], targetId?: string): CustomColumn[] {
  const target = columns.find((c) => c.id === targetId);
  return target ? columns.filter((c) => c.type === target.type && c.id !== target.id) : [];
}

/**
 * The columns an action of `type` may write. `move_to_group` writes a group,
 * and a notify or comment writes no cell, so those have none.
 */
export function actionTargetColumns(type: ActionType, columns: CustomColumn[]): CustomColumn[] {
  if (type === 'move_to_group' || isEffectAction(type)) return [];
  return ACTION_TARGET_COLUMNS[type](columns);
}

/** Sentinel for the trigger's "to any value" choice (Radix forbids empty ids). */
// eslint-disable-next-line i18next/no-literal-string -- option id sentinel
export const ANY_OPTION = '__any__';

/** A board whose rows are records the platform holds, not rows anyone adds here. */
const recordBacked = (boardType: TrackerBoardType) =>
  boardType === 'projects' || boardType === 'assets';

/**
 * Nothing on a record board creates a row, so an `item_created` rule there would
 * read as enabled and never fire. Only an assets board's rows have an approval
 * chain to decide on.
 */
export function triggerOptionsFor(boardType: TrackerBoardType): typeof TRIGGER_OPTIONS {
  return TRIGGER_OPTIONS.filter((o) => {
    if (o.type === 'item_created') return !recordBacked(boardType);
    if (o.type === 'approval_event') return boardType === 'assets';
    return true;
  });
}

/**
 * The record's own fields a rule on a record board can watch, shaped as columns
 * so the trigger pickers list them beside the board's. Ids are the row source's
 * field names, which is what the server diffs a record change under. Actions
 * never see these: a rule only writes the board's own columns. A field the
 * board renamed is listed under the name its header shows.
 */
export function recordTriggerColumns(
  itemSource: string | null | undefined,
  columnLabels: Readonly<Record<string, string>> = {},
): TriggerColumn[] {
  return recordFieldsFor(itemSource)
    .filter((field) => field.trigger === true)
    .map((field): TriggerColumn => {
      const label = columnLabels[field.id] ?? i18n.t(`trackers:${field.labelKey}`);
      const closed = CLOSED_TRIGGER_FIELDS[itemSource ?? '']?.[field.id];
      if (closed) {
        return { id: field.id, label, source: 'record', ...closed() };
      }
      return {
        id: field.id,
        label,
        source: 'record',
        type: field.type,
        statusOptions:
          field.type === 'status' ? recordStatusOptions(itemSource, field.id) : undefined,
      };
    });
}

/**
 * Record fields whose trigger offers a fixed list rather than what the board
 * cell shows. A project's type is free text on the board but one of a set of
 * ids on the wire, so "becomes Brand strategy" typed by hand would never match
 * `brand_strategy`. An asset's review status only ever reaches a rule as to
 * review or approved: nothing in the platform reports a rejection or "not
 * applicable", and a rule waiting on one would never fire.
 */
const CLOSED_TRIGGER_FIELDS: Readonly<
  Partial<
    Record<string, Partial<Record<string, () => Pick<CustomColumn, 'type' | 'statusOptions'>>>>
  >
> = {
  projects: {
    type: () => ({
      type: 'status',
      statusOptions: Object.entries(PROJECT_TYPE_MAP).map(([id, label]) => ({
        id,
        label,
        color: STATUS_COLORS.slate,
      })),
    }),
  },
  assets: {
    reviewStatus: () => ({
      type: 'status',
      // eslint-disable-next-line i18next/no-literal-string -- source and field ids
      statusOptions: recordStatusOptions('assets', 'reviewStatus').filter(
        (option) => option.id === 'to_review' || option.id === 'approved',
      ),
    }),
  },
};

/**
 * Whether a board's rows have a creator a rule could assign. Record rows are
 * the platform's, and the engine has nobody to name for them.
 */
export const boardHasRowCreators = (boardType: TrackerBoardType) => !recordBacked(boardType);

/** Whether conditions may use `is not`: a record board's own filter cannot express it. */
export const conditionsAllowNegation = (boardType: TrackerBoardType) => !recordBacked(boardType);

/** The board columns a condition can name: those the board's filter can filter by. */
export const conditionableColumns = (cols: CustomColumn[]) =>
  cols.filter((c) => filterKindForColumnType(c.type) !== null);

export function blankTrigger(
  columns: CustomColumn[],
  boardType: TrackerBoardType,
): AutomationTrigger {
  const status = statusColumns(columns).at(0);
  if (status) return { type: 'status_changed', columnId: status.id };
  return recordBacked(boardType)
    ? { type: 'value_changed', columnId: valueColumns(columns).at(0)?.id }
    : { type: 'item_created' };
}

/**
 * Whether the trigger names something to watch. `item_created` watches the board
 * rather than a column; every other type is inert without one, and a record
 * board that owns no columns yet is where `blankTrigger` hands back exactly
 * that. Saving it would leave a rule reading as enabled that can never fire.
 */
export function triggerIsComplete(trigger: AutomationTrigger): boolean {
  if (trigger.type === 'schedule') return trigger.hour !== undefined;
  return (
    trigger.type === 'item_created' ||
    trigger.type === 'approval_event' ||
    Boolean(trigger.columnId)
  );
}

/** A number as a value trigger reads one: plain digits, no thousands separator. */
const asNumber = (raw: string | undefined) => {
  if (raw === undefined || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Whether a value trigger's operator has what it compares with. The engine
 * matches no row against a blank value, a number operator against text, or a
 * Between whose bounds are missing or reversed, so such a rule saves and never
 * fires.
 */
export function triggerValueIsComplete(trigger: AutomationTrigger): boolean {
  if (trigger.type !== 'value_changed') return true;
  const operator = valueOperatorOf(trigger);
  if (!operator || !operatorTakesValue(operator)) return true;
  if (operator === 'equals' || operator === 'contains')
    return (trigger.toValue ?? '').trim() !== '';
  const low = asNumber(trigger.toValue);
  if (low === null) return false;
  if (operator !== 'between') return true;
  const high = asNumber(trigger.toValueMax);
  return high !== null && low <= high;
}

/** Triggers no person sets off, so there is nobody to be "whoever made the change". */
export const isTimedTrigger = (trigger: AutomationTrigger) =>
  trigger.type === 'schedule' || trigger.type === 'date_arrives';

/** Whether every condition names a column the board can filter by and at least one value. */
export function conditionsAreComplete(
  conditions: AutomationConditions | null | undefined,
  columns: CustomColumn[],
): boolean {
  return conditionsAreReadable(conditions as RuleConditions | null | undefined, columns);
}

/**
 * What a saved rule points at that the board no longer has. Columns and
 * options get deleted after a rule is saved, and the rule keeps reading
 * "Active" while it either never fires or writes into a field nobody sees.
 */
export type BrokenReference =
  | 'triggerColumn'
  | 'triggerOption'
  | 'conditionColumn'
  | 'actionColumn'
  | 'actionOption'
  | 'group';

export function brokenReference(
  rule: {
    trigger: AutomationTrigger;
    actions: AutomationAction[];
    conditions?: AutomationConditions | null;
  },
  board: { columns: CustomColumn[]; triggerColumns: CustomColumn[]; groups: StatusOption[] },
): BrokenReference | null {
  const { trigger, actions, conditions } = rule;
  const watched = trigger.columnId
    ? board.triggerColumns.find((c) => c.id === trigger.columnId)
    : undefined;
  if (trigger.columnId && !watched) return 'triggerColumn';
  if (watched?.statusOptions) {
    const options = new Set(watched.statusOptions.map((o) => o.id));
    const named = [trigger.toOptionId, trigger.fromOptionId, trigger.exceptOptionId];
    if (named.some((id) => id && id !== ANY_OPTION && !options.has(id))) return 'triggerOption';
  }
  if (!conditionsAreComplete(conditions, board.columns)) return 'conditionColumn';
  for (const action of actions) {
    if (action.type === 'move_to_group') {
      if (action.groupId && !board.groups.some((g) => g.id === action.groupId)) return 'group';
      continue;
    }
    if (isEffectAction(action.type)) {
      const missing = (action.recipientColumnIds ?? []).some(
        (id) => !board.columns.some((c) => c.id === id),
      );
      if (missing) return 'actionColumn';
      continue;
    }
    const target = board.columns.find((c) => c.id === action.columnId);
    if (action.columnId && !target) return 'actionColumn';
    if (action.sourceColumnId && !board.columns.some((c) => c.id === action.sourceColumnId)) {
      return 'actionColumn';
    }
    if (
      action.type === 'set_status' &&
      action.optionId &&
      target?.statusOptions &&
      !target.statusOptions.some((o) => o.id === action.optionId)
    ) {
      return 'actionOption';
    }
  }
  return null;
}

/** Triggers a rule's own write can never set off: they wait on a clock or a decision. */
const TRIGGERS_NO_WRITE_WAKES: ReadonlySet<TriggerType> = new Set([
  'date_arrives',
  'schedule',
  'approval_event',
]);

/** A row added or copied with no board named lands on this one, where it is a new row. */
const addsRowHere = (action: AutomationAction) =>
  (action.type === 'create_row' || action.type === 'duplicate_row') && !action.targetTrackerId;

type RuleShape = {
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  timeZone?: string | null;
};

/**
 * Whether one of `writer`'s actions could set off `watcher`. A status or
 * checkbox trigger waiting for one value is only woken by a write of that
 * value, so a rule that sets Status to Done does not wake one waiting for
 * Approved.
 */
function wakes(writer: RuleShape, watcher: RuleShape): boolean {
  const { trigger } = watcher;
  if (trigger.type === 'item_created') return writer.actions.some(addsRowHere);
  if (TRIGGERS_NO_WRITE_WAKES.has(trigger.type) || !trigger.columnId) return false;
  return writer.actions.some((action) => {
    if (action.type === 'move_to_group' || action.columnId !== trigger.columnId) return false;
    if (
      trigger.type === 'status_changed' &&
      trigger.toOptionId &&
      trigger.toOptionId !== ANY_OPTION
    ) {
      return action.type !== 'set_status' || action.optionId === trigger.toOptionId;
    }
    if (trigger.type === 'checkbox_changed' && trigger.toChecked !== undefined) {
      return action.type !== 'set_checkbox' || action.value === trigger.toChecked;
    }
    return true;
  });
}

/** A narrowing a trigger leaves unset, or set to "any", matches every value. */
const narrowing = <T>(value: T | undefined): T | undefined =>
  value === ANY_OPTION ? undefined : value;

/** Two narrowings exclude each other only when both are set and differ. */
const disjoint = <T>(a: T | undefined, b: T | undefined) => {
  const x = narrowing(a);
  const y = narrowing(b);
  return x !== undefined && y !== undefined && x !== y;
};

/**
 * Whether one change could fire both triggers, so the two rules' writes land
 * together. Anything that keeps them apart (a different option, day, hour or
 * edge) means only one of them ever runs on a given change.
 */
function fireTogether(ruleA: RuleShape, ruleB: RuleShape): boolean {
  const a = ruleA.trigger;
  const b = ruleB.trigger;
  if (a.type !== b.type || a.columnId !== b.columnId) return false;
  // Each rule reads its hour and weekday on its own clock, so across zones they can meet.
  const sameClock = (ruleA.timeZone ?? 'UTC') === (ruleB.timeZone ?? 'UTC');
  if (
    disjoint(a.toOptionId, b.toOptionId) ||
    disjoint(a.fromOptionId, b.fromOptionId) ||
    disjoint(a.toChecked, b.toChecked) ||
    disjoint(a.toState, b.toState) ||
    disjoint(a.approvalEvent, b.approvalEvent) ||
    (sameClock && disjoint(a.hour, b.hour))
  ) {
    return false;
  }
  if (a.toOptionId && a.toOptionId === b.exceptOptionId) return false;
  if (b.toOptionId && b.toOptionId === a.exceptOptionId) return false;
  if (a.type === 'date_arrives') {
    if ((a.dayOffset ?? 0) !== (b.dayOffset ?? 0)) return false;
    if ((a.dateEdge ?? 'start') !== (b.dateEdge ?? 'start')) return false;
  }
  if (sameClock && a.weekdays?.length && b.weekdays?.length) {
    if (!a.weekdays.some((day) => b.weekdays?.includes(day))) return false;
  }
  // Only two exact values are sure never to meet; ranges and contains can overlap.
  if (
    valueOperatorOf(a) === 'equals' &&
    valueOperatorOf(b) === 'equals' &&
    disjoint(a.toValue, b.toValue)
  ) {
    return false;
  }
  return true;
}

const writtenColumns = (rule: RuleShape) =>
  new Set(
    rule.actions.flatMap((a) => (a.type !== 'move_to_group' && a.columnId ? [a.columnId] : [])),
  );

export interface RuleInteractions {
  /**
   * Ids of the other rules on a cycle back to the draft, in the order the
   * writes run. Empty with `loops` set means the draft wakes itself.
   */
  loop: { loops: boolean; through: string[] };
  /** Other rules one change can fire with the draft that write a column the draft writes too. */
  conflicts: Array<{ ruleId: string; columnId: string; draftWins: boolean }>;
}

/**
 * How a draft would interact with the board's other enabled rules: a loop,
 * where its writes wake a chain of rules that wakes it again (the engine cuts
 * it silently after a few hops), and conflicts, where another rule the same
 * change fires writes the same column and only one value survives. `draftPosition`
 * is where the draft sits in the order; a new rule goes last.
 */
export function ruleInteractions(
  draft: RuleShape,
  others: Array<RuleShape & { id: string; position: number; enabled: boolean }>,
  draftPosition: number,
): RuleInteractions {
  const live = others.filter((rule) => rule.enabled);
  const loop = { loops: wakes(draft, draft), through: [] as string[] };
  if (!loop.loops) {
    // Breadth-first from the draft over "wakes", stopping at the first rule
    // that wakes the draft back, so the shortest cycle is the one named.
    const cameFrom = new Map<string, string | null>();
    let frontier = live.filter((rule) => wakes(draft, rule));
    frontier.forEach((rule) => cameFrom.set(rule.id, null));
    while (frontier.length > 0) {
      const closing = frontier.find((rule) => wakes(rule, draft));
      if (closing) {
        const path: string[] = [];
        for (let id: string | null = closing.id; id; id = cameFrom.get(id) ?? null)
          path.unshift(id);
        loop.loops = true;
        loop.through = path;
        break;
      }
      const next = live.filter(
        (rule) => !cameFrom.has(rule.id) && frontier.some((from) => wakes(from, rule)),
      );
      next.forEach((rule) =>
        cameFrom.set(rule.id, frontier.find((from) => wakes(from, rule))?.id ?? null),
      );
      frontier = next;
    }
  }

  const draftWrites = writtenColumns(draft);
  const conflicts = live.flatMap((rule) => {
    if (!fireTogether(rule, draft)) return [];
    const shared = [...writtenColumns(rule)].filter((id) => draftWrites.has(id));
    return shared.map((columnId) => ({
      ruleId: rule.id,
      columnId,
      draftWins: draftPosition > rule.position,
    }));
  });
  return { loop, conflicts };
}

/** Why a draft rule cannot be saved yet, in the order the builder reads top to bottom. */
export type RuleProblem =
  | 'triggerColumn'
  | 'triggerValue'
  | 'conditions'
  | 'actionTarget'
  | 'actorOnTimedTrigger'
  | 'aiFillTriggersItself';

export function ruleProblem(
  trigger: AutomationTrigger,
  actions: AutomationAction[],
  conditions: AutomationConditions | null | undefined,
  columns: CustomColumn[],
): RuleProblem | null {
  if (!triggerIsComplete(trigger)) return 'triggerColumn';
  if (!triggerValueIsComplete(trigger)) return 'triggerValue';
  if (!conditionsAreComplete(conditions, columns)) return 'conditions';
  if (!actionsAreComplete(actions, columns)) return 'actionTarget';
  if (
    isTimedTrigger(trigger) &&
    actions.some((a) => a.userRef === 'actor' || a.recipientRefs?.includes('actor'))
  ) {
    return 'actorOnTimedTrigger';
  }
  if (
    (trigger.type === 'value_changed' || trigger.type === 'status_changed') &&
    actions.some((a) => a.type === 'ai_fill' && a.columnId === trigger.columnId)
  ) {
    return 'aiFillTriggersItself';
  }
  return null;
}

/**
 * Whether every action names what it writes. The engine skips an action with no
 * target, so a rule built only of those saves, reads as enabled and does
 * nothing — the same trap `triggerIsComplete` closes on the other side. A board
 * owning no column any action can target is where `blankAction` hands back
 * exactly that.
 *
 * An action aimed at a board column its type may not write also counts as
 * unfinished, a `set_date_today` saved against a date range being the case
 * that happens: the picker lists no such option, and the server refuses it.
 */
export function actionsAreComplete(actions: AutomationAction[], columns: CustomColumn[]): boolean {
  if (actions.length === 0) return false;
  return actions.every((action) => {
    if (action.type === 'move_to_group') return Boolean(action.groupId);
    if (action.type === 'notify') return notifyHasRecipients(action);
    if (action.type === 'comment') return (action.message ?? '').trim() !== '';
    if (action.type === 'webhook' || action.type === 'slack') return actionHasUrl(action);
    if (action.type === 'create_row') return (action.textValue ?? '').trim() !== '';
    if (action.type === 'move_row') return Boolean(action.targetTrackerId);
    if (action.type === 'duplicate_row' || action.type === 'archive_row') return true;
    if (action.type === 'run_agent') {
      return Boolean(action.agentId) && (action.agentInput ?? '').trim() !== '';
    }
    if (action.type === 'ai_fill') {
      if (!action.columnId || !action.aiMode || !(action.instruction ?? '').trim()) return false;
      const target = columns.find((c) => c.id === action.columnId);
      return !target || AI_MODE_COLUMN_TYPES[action.aiMode].includes(target.type);
    }
    if (!action.columnId) return false;
    if (action.type === 'copy_value' && !action.sourceColumnId) return false;
    if (!columns.some((c) => c.id === action.columnId)) return true;
    return actionTargetColumns(action.type, columns).some((c) => c.id === action.columnId);
  });
}

/**
 * A rule whose action writes the same column its trigger watches fires on every
 * edit of that column and puts the old value straight back, so the user's change
 * reads as "it didn't save". Given the trigger, the blank action steers clear of
 * the watched column; when nothing else is writable it leaves the target unset
 * (the engine skips an action with no column) rather than pre-arming the loop.
 */
export function blankAction(
  columns: CustomColumn[],
  trigger?: AutomationTrigger,
): AutomationAction {
  const unwatched = (column: CustomColumn) => column.id !== trigger?.columnId;
  // A board with no column of its own to write still has someone to tell.
  const writable = ACTION_OPTIONS.some((o) => actionTargetColumns(o.type, columns).length > 0);
  if (!writable) return { type: 'notify', userIds: [] };
  const status = actionTargetColumns('set_status', columns).find(unwatched);
  return status
    ? { type: 'set_status', columnId: status.id, optionId: status.statusOptions?.[0]?.id }
    : {
        type: 'set_date_today',
        columnId: actionTargetColumns('set_date_today', columns).find(unwatched)?.id,
      };
}

/**
 * The zone a new rule's dates are read in: the browser's, under its canonical
 * name, so one zone is stored under one spelling. Undefined when the browser
 * reports a zone it cannot resolve itself (some report `Etc/Unknown`), which
 * the server would refuse: the rule is still created, and its dates read as UTC.
 */
export function authorTimeZone(): string | undefined {
  const reported = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (!reported) return undefined;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: reported }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

const columnLabel = (columns: CustomColumn[], id?: string) =>
  columns.find((c) => c.id === id)?.label ??
  i18n.t('trackers:trackersBoard.automationModel.aColumn');
const optionLabel = (columns: CustomColumn[], columnId?: string, optionId?: string) =>
  statusColumns(columns)
    .find((c) => c.id === columnId)
    ?.statusOptions?.find((o) => o.id === optionId)?.label ??
  i18n.t('trackers:trackersBoard.automationModel.aValue');
const groupLabel = (groups: StatusOption[], id?: string) =>
  groups.find((g) => g.id === id)?.label ?? i18n.t('trackers:trackersBoard.automationModel.aGroup');

/** ISO weekdays, 1 = Monday, in the order a week reads. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/* eslint-disable i18next/no-literal-string -- key segments, Monday first */
const WEEKDAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
/* eslint-enable i18next/no-literal-string */

export const weekdayLabel = (day: number) =>
  i18n.t(`trackers:trackersBoard.automationModel.weekdays.${WEEKDAY_NAMES[day - 1]}`);

/** The weekday as it reads inside a sentence, which Spanish and Portuguese write in lower case. */
const weekdayInSentence = (day: number) =>
  i18n.t(`trackers:trackersBoard.automationModel.weekdaysInSentence.${WEEKDAY_NAMES[day - 1]}`);

/** An hour of the day as the builder shows it, `09:00`. */
export const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

const APPROVAL_STATE_KEYS: Record<AutomationApprovalState, ParseKeys<'trackers'>> = {
  pending: 'trackersBoard.automationModel.approvalStates.pending',
  approved: 'trackersBoard.automationModel.approvalStates.approved',
  rejected: 'trackersBoard.automationModel.approvalStates.rejected',
};

export const APPROVAL_STATES = Object.keys(APPROVAL_STATE_KEYS) as AutomationApprovalState[];
export const approvalStateLabel = (state: AutomationApprovalState) =>
  i18n.t(`trackers:${APPROVAL_STATE_KEYS[state]}`);

const OPERATOR_KEYS: Record<AutomationValueOperator, ParseKeys<'trackers'>> = {
  equals: 'trackersBoard.automationModel.operators.equals',
  greater_than: 'trackersBoard.automationModel.operators.greaterThan',
  less_than: 'trackersBoard.automationModel.operators.lessThan',
  between: 'trackersBoard.automationModel.operators.between',
  contains: 'trackersBoard.automationModel.operators.contains',
  is_empty: 'trackersBoard.automationModel.operators.isEmpty',
  is_not_empty: 'trackersBoard.automationModel.operators.isNotEmpty',
};

export const operatorLabel = (operator: AutomationValueOperator) =>
  i18n.t(`trackers:${OPERATOR_KEYS[operator]}`);

/**
 * The operator a value trigger compares with, or `undefined` when it fires on
 * any change. A rule saved before operators existed carries only a `toValue`,
 * which always meant an exact match.
 */
export const valueOperatorOf = (trigger: AutomationTrigger): AutomationValueOperator | undefined =>
  trigger.operator ?? (trigger.toValue === undefined ? undefined : 'equals');

function summarizeValueTrigger(trigger: AutomationTrigger, column: string): string {
  const operator = valueOperatorOf(trigger);
  const value = trigger.toValue ?? '';
  switch (operator) {
    case undefined:
      return i18n.t('trackers:trackersBoard.automationModel.columnChanges', { column });
    case 'equals':
      return i18n.t('trackers:trackersBoard.automationModel.columnBecomesQuotedValue', {
        column,
        value,
      });
    case 'greater_than':
      return i18n.t('trackers:trackersBoard.automationModel.columnBecomesGreaterThan', {
        column,
        value,
      });
    case 'less_than':
      return i18n.t('trackers:trackersBoard.automationModel.columnBecomesLessThan', {
        column,
        value,
      });
    case 'between':
      return i18n.t('trackers:trackersBoard.automationModel.columnBecomesBetween', {
        column,
        min: value,
        max: trigger.toValueMax ?? '',
      });
    case 'contains':
      return i18n.t('trackers:trackersBoard.automationModel.columnComesToContain', {
        column,
        value,
      });
    case 'is_empty':
      return i18n.t('trackers:trackersBoard.automationModel.columnIsEmptied', { column });
    case 'is_not_empty':
      return i18n.t('trackers:trackersBoard.automationModel.columnIsFilledIn', { column });
  }
}

function summarizeDateTrigger(trigger: AutomationTrigger, columns: CustomColumn[]): string {
  const column = columnLabel(columns, trigger.columnId);
  const offset = trigger.dayOffset ?? 0;
  const edged = hasDateEdges(columns.find((c) => c.id === trigger.columnId));
  const end = trigger.dateEdge === 'end';
  if (offset > 0) {
    const key = !edged
      ? 'trackers:trackersBoard.automationModel.daysBeforeColumn'
      : end
        ? 'trackers:trackersBoard.automationModel.daysBeforeEndOf'
        : 'trackers:trackersBoard.automationModel.daysBeforeStartOf';
    return i18n.t(key, { count: offset, column });
  }
  if (offset < 0) {
    const key = !edged
      ? 'trackers:trackersBoard.automationModel.daysAfterColumn'
      : end
        ? 'trackers:trackersBoard.automationModel.daysAfterEndOf'
        : 'trackers:trackersBoard.automationModel.daysAfterStartOf';
    return i18n.t(key, { count: -offset, column });
  }
  const key = !edged
    ? 'trackers:trackersBoard.automationModel.columnArrives'
    : end
      ? 'trackers:trackersBoard.automationModel.endOfColumnArrives'
      : 'trackers:trackersBoard.automationModel.startOfColumnArrives';
  return i18n.t(key, { column });
}

/**
 * The hour a schedule fires at, with its zone when that is not the reader's:
 * the hour is read on the author's clock, and a bare 09:00 would mislead anyone
 * elsewhere.
 */
export function scheduleTimeLabel(hour: number, timeZone: string | undefined): string {
  const time = hourLabel(hour);
  if (!timeZone || timeZone === authorTimeZone()) return time;
  return i18n.t('trackers:trackersBoard.automationModel.timeInZone', { time, zone: timeZone });
}

function summarizeSchedule(trigger: AutomationTrigger, timeZone: string | undefined): string {
  const hour = trigger.hour ?? 0;
  const time = scheduleTimeLabel(hour, timeZone);
  const days = [...(trigger.weekdays ?? [])].sort((a, b) => a - b);
  // Counted by the hour, which is what "it is one o'clock" agrees with.
  if (days.length === 0 || days.length === WEEKDAYS.length) {
    return i18n.t('trackers:trackersBoard.automationModel.everyDayAt', { time, count: hour });
  }
  return i18n.t('trackers:trackersBoard.automationModel.everyWeekdaysAt', {
    days: days
      .map(weekdayInSentence)
      .join(i18n.t('trackers:trackersBoard.automationModel.listSeparator')),
    time,
    count: hour,
  });
}

export function summarizeTrigger(
  trigger: AutomationTrigger,
  columns: CustomColumn[],
  timeZone?: string,
): string {
  switch (trigger.type) {
    case 'status_changed': {
      const column = columnLabel(columns, trigger.columnId);
      const option = (id: string) => optionLabel(columns, trigger.columnId, id);
      if (trigger.fromOptionId && trigger.toOptionId) {
        return i18n.t('trackers:trackersBoard.automationModel.columnChangesFromTo', {
          column,
          from: option(trigger.fromOptionId),
          to: option(trigger.toOptionId),
        });
      }
      if (trigger.fromOptionId && trigger.exceptOptionId) {
        return i18n.t('trackers:trackersBoard.automationModel.columnChangesFromToAnythingBut', {
          column,
          from: option(trigger.fromOptionId),
          value: option(trigger.exceptOptionId),
        });
      }
      if (trigger.exceptOptionId) {
        return i18n.t('trackers:trackersBoard.automationModel.columnBecomesAnythingBut', {
          column,
          value: option(trigger.exceptOptionId),
        });
      }
      if (trigger.fromOptionId) {
        return i18n.t('trackers:trackersBoard.automationModel.columnChangesFrom', {
          column,
          from: option(trigger.fromOptionId),
        });
      }
      return trigger.toOptionId
        ? i18n.t('trackers:trackersBoard.automationModel.columnBecomesValue', {
            column,
            value: option(trigger.toOptionId),
          })
        : i18n.t('trackers:trackersBoard.automationModel.columnChanges', { column });
    }
    case 'checkbox_changed':
      if (trigger.toChecked === undefined) {
        return i18n.t('trackers:trackersBoard.automationModel.columnChanges', {
          column: columnLabel(columns, trigger.columnId),
        });
      }
      return trigger.toChecked
        ? i18n.t('trackers:trackersBoard.automationModel.columnIsChecked', {
            column: columnLabel(columns, trigger.columnId),
          })
        : i18n.t('trackers:trackersBoard.automationModel.columnIsUnchecked', {
            column: columnLabel(columns, trigger.columnId),
          });
    case 'button_clicked':
      return i18n.t('trackers:trackersBoard.automationModel.columnIsClicked', {
        column: columnLabel(columns, trigger.columnId),
      });
    case 'people_changed':
      return i18n.t('trackers:trackersBoard.automationModel.columnChanges', {
        column: columnLabel(columns, trigger.columnId),
      });
    case 'value_changed':
      return summarizeValueTrigger(trigger, columnLabel(columns, trigger.columnId));
    case 'item_created':
      return i18n.t('trackers:trackersBoard.automationModel.anItemIsCreated');
    case 'date_arrives':
      return summarizeDateTrigger(trigger, columns);
    case 'approval_changed':
      return trigger.toState
        ? i18n.t('trackers:trackersBoard.automationModel.columnIsMarkedState', {
            column: columnLabel(columns, trigger.columnId),
            state: approvalStateLabel(trigger.toState).toLowerCase(),
          })
        : i18n.t('trackers:trackersBoard.automationModel.columnChanges', {
            column: columnLabel(columns, trigger.columnId),
          });
    case 'files_added':
      return i18n.t('trackers:trackersBoard.automationModel.aFileIsAddedToColumn', {
        column: columnLabel(columns, trigger.columnId),
      });
    case 'schedule':
      return summarizeSchedule(trigger, timeZone);
    case 'approval_event': {
      const option = APPROVAL_EVENT_OPTIONS.find((o) => o.event === trigger.approvalEvent);
      return option
        ? i18n.t(`trackers:${option.summaryKey}`)
        : i18n.t('trackers:trackersBoard.automationModel.anApprovalIsDecided');
    }
    default:
      return i18n.t('trackers:trackersBoard.automationModel.somethingChanges');
  }
}

/**
 * Whether a shift or an add goes back. The direction lives in the sign, and a
 * cleared amount under Earlier or Subtract is -0, which `< 0` reads as forward.
 */
export const isBackwards = (n: number | undefined): boolean =>
  n !== undefined && (n < 0 || Object.is(n, -0));

export function summarizeAction(
  action: AutomationAction,
  columns: CustomColumn[],
  groups: StatusOption[],
): string {
  switch (action.type) {
    case 'set_status':
      return i18n.t('trackers:trackersBoard.automationModel.setColumnToValue', {
        column: columnLabel(columns, action.columnId),
        value: optionLabel(columns, action.columnId, action.optionId),
      });
    case 'move_to_group':
      return i18n.t('trackers:trackersBoard.automationModel.moveItToGroup', {
        group: groupLabel(groups, action.groupId),
      });
    case 'set_checkbox':
      return action.value
        ? i18n.t('trackers:trackersBoard.automationModel.checkColumn', {
            column: columnLabel(columns, action.columnId),
          })
        : i18n.t('trackers:trackersBoard.automationModel.uncheckColumn', {
            column: columnLabel(columns, action.columnId),
          });
    case 'set_date_today':
      return (action.dayOffset ?? 0) > 0
        ? i18n.t('trackers:trackersBoard.automationModel.setColumnToTodayPlus', {
            column: columnLabel(columns, action.columnId),
            count: action.dayOffset,
          })
        : i18n.t('trackers:trackersBoard.automationModel.setColumnToToday', {
            column: columnLabel(columns, action.columnId),
          });
    case 'assign_people':
      if (action.userRef) {
        return i18n.t(
          action.userRef === 'actor'
            ? 'trackers:trackersBoard.automationModel.assignTheEditorToColumn'
            : 'trackers:trackersBoard.automationModel.assignTheCreatorToColumn',
          { column: columnLabel(columns, action.columnId) },
        );
      }
      return (action.userIds?.length ?? 0) > 0
        ? i18n.t('trackers:trackersBoard.automationModel.assignPeopleToColumn', {
            people: action.userIds?.length ?? 0,
            column: columnLabel(columns, action.columnId),
          })
        : i18n.t('trackers:trackersBoard.automationModel.clearPeopleOnColumn', {
            column: columnLabel(columns, action.columnId),
          });
    case 'set_number':
      return i18n.t('trackers:trackersBoard.automationModel.setColumnToValue', {
        column: columnLabel(columns, action.columnId),
        value: action.numberValue ?? 0,
      });
    case 'set_text':
      return i18n.t('trackers:trackersBoard.automationModel.setColumnToQuotedValue', {
        column: columnLabel(columns, action.columnId),
        value: action.textValue ?? '',
      });
    case 'clear_value':
      return i18n.t('trackers:trackersBoard.automationModel.clearColumn', {
        column: columnLabel(columns, action.columnId),
      });
    case 'shift_date': {
      const days = action.dayOffset ?? 0;
      return i18n.t(
        isBackwards(days)
          ? 'trackers:trackersBoard.automationModel.moveColumnEarlier'
          : 'trackers:trackersBoard.automationModel.moveColumnLater',
        { column: columnLabel(columns, action.columnId), count: Math.abs(days) },
      );
    }
    case 'increment_number': {
      const by = action.numberValue ?? 0;
      return i18n.t(
        isBackwards(by)
          ? 'trackers:trackersBoard.automationModel.subtractFromColumn'
          : 'trackers:trackersBoard.automationModel.addToColumn',
        { column: columnLabel(columns, action.columnId), value: Math.abs(by) },
      );
    }
    case 'copy_value':
      return i18n.t('trackers:trackersBoard.automationModel.copySourceIntoColumn', {
        source: columnLabel(columns, action.sourceColumnId),
        column: columnLabel(columns, action.columnId),
      });
    case 'notify':
      return i18n.t('trackers:trackersBoard.automationModel.notifyRecipients', {
        recipients: summarizeRecipients(action, columns),
      });
    case 'comment':
      return i18n.t('trackers:trackersBoard.automationModel.commentQuotedText', {
        text: action.message ?? '',
      });
    case 'webhook':
      return i18n.t('trackers:trackersBoard.automationModel.sendAWebhookTo', {
        host: urlHost(action),
      });
    case 'slack':
      return i18n.t('trackers:trackersBoard.automationModel.postTheRowToSlack');
    case 'create_row':
      return action.targetTrackerId
        ? i18n.t('trackers:trackersBoard.automationModel.addARowToAnotherBoard', {
            title: action.textValue ?? '',
          })
        : i18n.t('trackers:trackersBoard.automationModel.addARow', {
            title: action.textValue ?? '',
          });
    case 'duplicate_row':
      return action.targetTrackerId
        ? i18n.t('trackers:trackersBoard.automationModel.copyTheRowToAnotherBoard')
        : i18n.t('trackers:trackersBoard.automationModel.copyTheRow');
    case 'move_row':
      return i18n.t('trackers:trackersBoard.automationModel.moveTheRowToAnotherBoard');
    case 'archive_row':
      return i18n.t('trackers:trackersBoard.automationModel.archiveTheRow');
    case 'run_agent':
      return i18n.t('trackers:trackersBoard.automationAi.agent.summary', {
        input: action.agentInput ?? '',
      });
    case 'ai_fill':
      return i18n.t('trackers:trackersBoard.automationAi.fill.summary', {
        column: columnLabel(columns, action.columnId),
        instruction: action.instruction ?? '',
      });
    default:
      return i18n.t('trackers:trackersBoard.automationModel.doSomething');
  }
}

/** The host a webhook posts to, from the URL typed or the saved one's preview. */
function urlHost(action: AutomationAction): string {
  const source = action.url ?? action.urlPreview;
  if (!source) return i18n.t('trackers:trackersBoard.automationModel.aUrl');
  try {
    return new URL(source).host;
  } catch {
    return i18n.t('trackers:trackersBoard.automationModel.aUrl');
  }
}

/** Who a notify action emails, as its sentence names them. */
function summarizeRecipients(action: AutomationAction, columns: CustomColumn[]): string {
  const parts: string[] = [
    ...(action.recipientRefs ?? []).map((ref) =>
      ref === 'actor'
        ? i18n.t('trackers:trackersBoard.automationModel.recipients.theEditor')
        : i18n.t('trackers:trackersBoard.automationModel.recipients.theCreator'),
    ),
    ...(action.recipientColumnIds ?? []).map((id) =>
      i18n.t('trackers:trackersBoard.automationModel.recipients.peopleInColumn', {
        column: columnLabel(columns, id),
      }),
    ),
  ];
  const named = action.userIds?.length ?? 0;
  if (named > 0) {
    parts.push(
      i18n.t('trackers:trackersBoard.automationModel.recipients.namedPeople', { count: named }),
    );
  }
  return parts.length > 0
    ? parts.join(i18n.t('trackers:trackersBoard.automationModel.listSeparator'))
    : i18n.t('trackers:trackersBoard.automationModel.recipients.nobodyYet');
}

/** Names for condition values a column cannot label by itself. */
export interface ConditionLabels {
  dateBuckets: Record<DateBucket, string>;
  personLabel: (id: string) => string | undefined;
}

function conditionValueLabel(
  column: CustomColumn,
  value: string,
  labels: ConditionLabels | undefined,
): string {
  if (column.type === 'status') {
    return column.statusOptions?.find((o) => o.id === value)?.label ?? value;
  }
  if ((DATE_BUCKETS as readonly string[]).includes(value) && labels) {
    return labels.dateBuckets[value as DateBucket].toLowerCase();
  }
  return labels?.personLabel(value) ?? i18n.t('trackers:trackersBoard.automationModel.someone');
}

/**
 * The conditions as a clause, or `null` when none would narrow anything. A
 * condition on a deleted column, or with no value chosen, is skipped exactly as
 * the engine skips it, so the sentence never names a check that does not run.
 */
export function summarizeConditions(
  conditions: AutomationConditions | null | undefined,
  columns: CustomColumn[],
  labels?: ConditionLabels,
): string | null {
  if (!conditions) return null;
  const byId = new Map(conditionableColumns(columns).map((c) => [c.id, c]));
  // Compiled by the matcher the engine runs, so the sentence names exactly the
  // conditions that are checked.
  const live = compileConditions(conditions.rules, filterMatchColumns(columns));
  const parts = live.flatMap((filter) => {
    const column = byId.get(filter.columnId);
    if (!column) return [];
    const values = [...filter.values]
      .map((value) => conditionValueLabel(column, value, labels))
      .join(i18n.t('trackers:trackersBoard.automationModel.valueSeparator'));
    return [
      i18n.t(
        filter.negate
          ? 'trackers:trackersBoard.automationModel.columnIsNotValues'
          : 'trackers:trackersBoard.automationModel.columnIsValues',
        { column: column.label, values },
      ),
    ];
  });
  if (parts.length === 0) return null;
  return parts.join(
    i18n.t(
      conditions.match === 'any'
        ? 'trackers:trackersBoard.automationModel.anyConditionSeparator'
        : 'trackers:trackersBoard.automationModel.allConditionSeparator',
    ),
  );
}

/** Full "When … , then … and …" sentence for a rule. */
export function summarize(
  trigger: AutomationTrigger,
  actions: AutomationAction[],
  columns: CustomColumn[],
  groups: StatusOption[],
  conditions?: AutomationConditions | null,
  labels?: ConditionLabels,
  /** The zone a schedule's hour is read in: the rule's, or the author's for a draft. */
  timeZone?: string,
): string {
  const then = actions.length
    ? actions
        .map((a) => summarizeAction(a, columns, groups))
        .join(i18n.t('trackers:trackersBoard.automationModel.actionSeparator'))
    : i18n.t('trackers:trackersBoard.automationModel.doNothingYet');
  const onlyIf = summarizeConditions(conditions, columns, labels);
  return onlyIf
    ? i18n.t('trackers:trackersBoard.automationModel.whenTriggerIfConditionsThenActions', {
        trigger: summarizeTrigger(trigger, columns, timeZone),
        conditions: onlyIf,
        actions: then,
      })
    : i18n.t('trackers:trackersBoard.automationModel.whenTriggerThenActions', {
        trigger: summarizeTrigger(trigger, columns, timeZone),
        actions: then,
      });
}
